import type { DescriptorEnumItem } from "@lr-notitia/notitia-model-store/schemas";
import type { Graph } from "@lr-notitia/notitia-engineer/schemas";
import type { GraphModification } from "@lr-notitia/notitia-engineer/schemas";
import { Ref, computed } from "vue";
import { useSymbolsStore } from "../symbols";
import * as Metrics from "../metrics";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import { api } from "@lr-notitia/notitia-engineer/client";
import { handleGraphModification } from "./utils";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const enumItems = (graph: Ref<Graph>) =>
	computed<DescriptorEnumItem[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "enum-item" && node.layer === "descriptor"
			) as DescriptorEnumItem[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});

export function enumItem(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => {
			Metrics.recordAccess(id);
			return graph.value.nodes[id] as DescriptorEnumItem | undefined;
		},
		add: (data: { refersTo: string; localTerminology: Terminology }) => add(domainId, graph, data),
		update: (enumItem: DescriptorEnumItem) => update(domainId, graph, enumItem),
		remove: (id: string) => remove(domainId, graph, id),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	data: { refersTo: string; localTerminology: Terminology }
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.enumItems()
			.post(data)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, enumItem: DescriptorEnumItem): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[enumItem.id]) return reject();
		api()
			.domain(domainId.value)
			.enumItem(enumItem.id)
			.put(enumItem)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.enumItem(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
